import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AlertCircle, User, Building2, Mail, Phone, MapPin, Check } from 'lucide-react';

export const ClientProfile = () => {
  const { user, company, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    companyName: company?.name || '',
    cnpj: company?.cnpj || '',
    phone: company?.phone || '',
    address: company?.address || '',
    city: company?.city || '',
    state: company?.state || '',
    zipCode: company?.zipCode || ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage('');

    try {
      await updateProfile({ name: formData.name });
      setMessage('Perfil atualizado com sucesso!');
      setIsEditing(false);
    } catch (error) {
      setMessage('Erro ao atualizar perfil. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Meus Dados | Portal Ethics</title>
      </Helmet>

      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Meus Dados</h1>
          <p className="text-neutral-600">Gerencie suas informações pessoais e da empresa</p>
        </div>

        {message && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${
            message.includes('sucesso') 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.includes('sucesso') ? (
              <Check className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm">{message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* User Info */}
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary-blue" />
              Dados Pessoais
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nome Completo"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={!isEditing}
                icon={<User className="w-4 h-4" />}
              />

              <Input
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={true}
                icon={<Mail className="w-4 h-4" />}
              />
            </div>
          </Card>

          {/* Company Info */}
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary-blue" />
              Dados da Empresa
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nome da Empresa"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                disabled={!isEditing}
                icon={<Building2 className="w-4 h-4" />}
              />

              <Input
                label="CNPJ"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                disabled={true}
              />

              <Input
                label="Telefone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={!isEditing}
                icon={<Phone className="w-4 h-4" />}
              />

              <Input
                label="Endereço"
                name="address"
                value={formData.address}
                onChange={handleChange}
                disabled={!isEditing}
                icon={<MapPin className="w-4 h-4" />}
              />

              <Input
                label="Cidade"
                name="city"
                value={formData.city}
                onChange={handleChange}
                disabled={!isEditing}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Estado"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  disabled={!isEditing}
                />

                <Input
                  label="CEP"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            {isEditing ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSaving}
                >
                  {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsEditing(true)}
              >
                Editar Dados
              </Button>
            )}
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};
